import React, { useMemo } from 'react';
import { Modal, ModalBody, ModalContent, ModalHeader } from '@nextui-org/react';
import { Chip } from '@nextui-org/react';
import { CtySpinner } from '@circuitry-ai/doc-ui';
import { AdjustmentConversationView } from './AdjustmentConversationView';
import { AdjustmentCostValidationView } from './AdjustmentCostValidationView';
import {
    extractCoverageConversationUrl,
    extractLaborHoursCheckPayload,
    extractSimDataSection,
    extractPartsCostValidationPayload,
    parseConversationLink,
    resolveAdjustmentEventContentKind,
    type AdjustmentLinkEvent,
    type TagAgentRun,
} from './adjustmentDetailsExtractors';
import { usePalDockOffset } from './usePalDockOffset';

type Props = {
    event: AdjustmentLinkEvent | null;
    /** Agent run matching event.agentName, fetched (and stored per agent) by the parent page. */
    agentRun: TagAgentRun | null;
    isLoading?: boolean;
    error?: string | null;
    onClose: () => void;
};

export const AdjustmentDetailsModal: React.FC<Props> = ({ event, agentRun, isLoading = false, error = null, onClose }) => {
    const isOpen = Boolean(event);
    usePalDockOffset(isOpen);

    const failureId = event?.data?.failureId;
    const detailId = event?.data?.detailId;
    const simData = event?.data?.simData ?? {};
    const adjustmentReason = typeof simData.adjustment_reason === 'string' ? simData.adjustment_reason : '';
    const contentKind = resolveAdjustmentEventContentKind(event);

    const conversationIds = useMemo(() => {
        if (contentKind !== 'conversation' || !agentRun) return null;
        const url = extractCoverageConversationUrl(agentRun.action_output, failureId, detailId);
        return url ? parseConversationLink(url) : null;
    }, [contentKind, agentRun, failureId, detailId]);

    const costPayload = useMemo(() => {
        if (contentKind === 'parts_cost') {
            return (
                (agentRun && extractPartsCostValidationPayload(agentRun.action_output, failureId, detailId)) ||
                extractSimDataSection(simData, 'parts_cost_validation')
            );
        }
        if (contentKind === 'labor_hours') {
            return (
                (agentRun && extractLaborHoursCheckPayload(agentRun.action_output, failureId, detailId)) ||
                extractSimDataSection(simData, 'labor_hours_check')
            );
        }
        return null;
    }, [contentKind, agentRun, failureId, detailId, simData]);

    const title =
        contentKind === 'parts_cost'
            ? 'Parts Cost Validation'
            : contentKind === 'labor_hours'
                ? 'Labor Hour Check'
                : 'Coverage Check';
    const subtitleParts = [
        failureId != null ? `Failure #${failureId}` : null,
        typeof simData.name === 'string' ? String(simData.name).trim() : null,
        adjustmentReason || null,
        detailId != null ? `Line ${detailId}` : null,
    ].filter(Boolean);

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="3xl"
            scrollBehavior="inside"
            classNames={{
                base: 'bg-content1 text-foreground',
                wrapper: 'adjustment-details-modal-wrapper',
                backdrop: 'adjustment-details-modal-backdrop',
            }}
        >
            <ModalContent>
                {() => (
                    <>
                        <ModalHeader className="flex flex-col items-start gap-1">
                            <Chip size="sm" color="secondary" variant="flat">
                                Simulated
                            </Chip>
                            <span className="text-foreground">{title}</span>
                            {subtitleParts.length > 0 && (
                                <span className="text-sm font-normal text-default-500">{subtitleParts.join(' · ')}</span>
                            )}
                        </ModalHeader>
                        <ModalBody className="pb-6">
                            {isLoading && (
                                <div className="flex justify-center py-8">
                                    <CtySpinner size="lg" />
                                </div>
                            )}
                            {!isLoading && error && <p className="text-default-500">{error}</p>}
                            {!isLoading && !error && contentKind === 'conversation' && conversationIds && (
                                <AdjustmentConversationView
                                    senderId={conversationIds.senderId}
                                    advisorId={conversationIds.advisorId}
                                    messageId={conversationIds.messageId}
                                />
                            )}
                            {!isLoading && !error && contentKind === 'conversation' && !conversationIds && (
                                <p className="text-default-500">Coverage conversation was not found for this line.</p>
                            )}
                            {!isLoading && !error && (contentKind === 'parts_cost' || contentKind === 'labor_hours') && costPayload && (
                                <AdjustmentCostValidationView
                                    payload={costPayload}
                                    timestamp={typeof agentRun?.timestamp === 'string' ? agentRun.timestamp : undefined}
                                    agentName={event?.agentName}
                                />
                            )}
                            {!isLoading && !error && (contentKind === 'parts_cost' || contentKind === 'labor_hours') && !costPayload && (
                                <p className="text-default-500">Validation details were not found for this line.</p>
                            )}
                            {!isLoading && !error && !contentKind && (
                                <p className="text-default-500">No details are mapped for this adjustment reason.</p>
                            )}
                        </ModalBody>
                    </>
                )}
            </ModalContent>
        </Modal>
    );
};
